import { App, Form, Input, Modal, Tag } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import CustomizedUpload from 'components/customized-upload';
import DebounceSelect from 'components/debounce-select/select';
import { UPLOAD_TYPE } from 'constants/common';
import { createNominee, listTickets, setNomineeMembers, updateNominee } from '../api';
import { GalaNominee, TICKET_STATUS_META, errorText } from '../contract';
import { fileUrl } from '../lib/file-url';

const { TextArea } = Input;

type UploadValue = { fileKey: string; fileUrl: string; error?: string };
type TicketOption = { value: number; label: string };

interface FormValues {
  displayName: string;
  tickets: TicketOption[];
  image?: UploadValue;
  note?: string;
}

interface Props {
  eventId: number;
  categoryId: number;
  /** Có = sửa đơn vị này; null = thêm mới. */
  nominee: GalaNominee | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const memberLabel = (displayName: string, username: string | null) =>
  username ? `${displayName} (@${username})` : displayName;

const toForm = (n: GalaNominee | null): FormValues => ({
  displayName: n?.displayName ?? '',
  tickets: (n?.members ?? []).map((m) => ({
    value: m.ticketId,
    label: memberLabel(m.displayName, m.username),
  })),
  image: n?.imageKey ? { fileKey: n.imageKey, fileUrl: fileUrl(n.imageKey) } : undefined,
  note: n?.note ?? '',
});

const uploadRule = {
  validator: (_: unknown, v?: UploadValue) =>
    v?.error ? Promise.reject(new Error(v.error)) : Promise.resolve(),
};

/** Modal Thêm / Sửa một đơn vị được đề cử: tên, vé thành viên (ô tìm), ảnh, ghi chú. */
const NomineeModal = ({ eventId, categoryId, nominee, open, onClose, onSaved }: Props) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  // preserve={false} + nạp lại mỗi lần mở: sửa đơn vị B không được hiện giá trị của đơn vị A.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(toForm(nominee));
  }, [open, nominee, form]);

  /** Tìm vé của sự kiện theo tên / @username / UID; vé đã thu hồi hiện nhưng không chọn được. */
  const fetchTickets = useCallback(
    async (search: string) => {
      const q = search.trim();
      if (!q) return [];
      try {
        const res = await listTickets(eventId, { q, page: 1, limit: 20 });
        return (res?.data ?? []).map((t) => {
          const meta = TICKET_STATUS_META[t.status];
          return {
            value: t.id,
            disabled: t.status === 'REVOKED',
            label: (
              <span>
                {memberLabel(t.displayName || t.shortCode, t.user?.username ?? null)}
                {t.tiktokUid ? ` · ${t.tiktokUid}` : ''}{' '}
                <Tag color={meta?.color}>{meta?.text ?? t.status}</Tag>
              </span>
            ),
          };
        });
      } catch {
        return [];
      }
    },
    [eventId]
  );

  const submit = async () => {
    let v: FormValues;
    try {
      v = await form.validateFields();
    } catch {
      return;
    }
    const ticketIds = (v.tickets ?? []).map((t) => Number(t.value));
    const displayName = v.displayName.trim();
    const imageKey = v.image?.fileKey || null;
    const note = (v.note ?? '').trim() || null;
    setSaving(true);
    try {
      if (nominee) {
        await updateNominee(eventId, nominee.id, { displayName, imageKey, note });
        const before = nominee.members
          .map((m) => m.ticketId)
          .sort((a, b) => a - b)
          .join(',');
        if (before !== [...ticketIds].sort((a, b) => a - b).join(',')) {
          await setNomineeMembers(eventId, nominee.id, ticketIds);
        }
        message.success('Đã lưu đơn vị được đề cử.');
      } else {
        await createNominee(eventId, categoryId, { displayName, ticketIds, imageKey, note });
        message.success('Đã thêm đề cử.');
      }
      await onSaved();
      onClose();
    } catch (e) {
      message.error(errorText(e, 'Không lưu được đề cử.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={nominee ? 'Sửa đề cử' : 'Thêm đề cử'}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Lưu"
      cancelText="Đóng"
      confirmLoading={saving}
      width={640}
      destroyOnClose
      maskClosable={false}
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={toForm(nominee)}
        requiredMark="optional"
      >
        <Form.Item
          name="displayName"
          label="Tên hiển thị"
          extra="Một Creator, một Nhóm LIVE hoặc một Agency. Ví dụ: Bé Na, HP Dance Team."
          rules={[
            { required: true, whitespace: true, message: 'Nhập tên hiển thị.' },
            { max: 120, message: 'Không quá 120 ký tự.' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="tickets"
          label="Vé thành viên"
          extra="Gõ tên, @username hoặc UID TikTok. Nhóm LIVE chọn nhiều vé; Agency chọn vé người đại diện."
          rules={[{ required: true, type: 'array', min: 1, message: 'Chọn ít nhất một vé.' }]}
        >
          <DebounceSelect
            mode="multiple"
            fetchOptions={fetchTickets}
            style={{ width: '100%' }}
            placeholder="Gõ để tìm vé mời…"
            aria-label="Tìm vé mời"
          />
        </Form.Item>
        <Form.Item
          name="image"
          label="Ảnh đại diện (tuỳ chọn)"
          extra="Không có thì app lấy ảnh tài khoản của thành viên đầu tiên."
          rules={[uploadRule]}
        >
          <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={5} />
        </Form.Item>
        <Form.Item
          name="note"
          label="Ghi chú nội bộ"
          rules={[{ max: 300, message: 'Không quá 300 ký tự.' }]}
        >
          <TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NomineeModal;
